/**
 * @license
 * Copyright Google LLC All Rights Reserved.
 *
 * Use of this source code is governed by an MIT-style license that can be
 * found in the LICENSE file at https://angular.io/license
 */

import { NgClass } from '@angular/common';
import { ActiveDescendantKeyManager } from '@angular/cdk/a11y';
import { coerceBooleanProperty } from '@angular/cdk/coercion';
import {
    AfterContentInit,
    ChangeDetectionStrategy,
    ChangeDetectorRef,
    Component,
    ContentChildren,
    ElementRef,
    EventEmitter,
    InjectionToken,
    Input,
    Output,
    QueryList,
    TemplateRef,
    ViewChild,
    input,
    inject
} from '@angular/core';

import { ApOption } from '../option/option.model';
import { _getOptionScrollPosition, AP_OPTION_PARENT_COMPONENT, ApAutocompleteOptionComponent } from '../option/option.component';

/**
 * Autocomplete IDs need to be unique across components, so this counter exists outside of
 * the component definition.
 */
let _uniqueAutocompleteIdCounter = 0;

/** Default `ap-autocomplete` options that can be overridden. */
export interface ApAutocompleteDefaultOptions {
    /** Whether the first option should be highlighted when an autocomplete panel is opened. */
    autoActiveFirstOption?: boolean;
}

/** @docs-private */
export const AP_AUTOCOMPLETE_DEFAULT_OPTIONS_FACTORY = (): ApAutocompleteDefaultOptions => ({
    autoActiveFirstOption: false
});

/** Injection token to be used to override the default options for `ap-autocomplete`. */
export const AP_AUTOCOMPLETE_DEFAULT_OPTIONS = new InjectionToken<ApAutocompleteDefaultOptions>('ap-autocomplete-default-options', {
    providedIn: 'root',
    factory: AP_AUTOCOMPLETE_DEFAULT_OPTIONS_FACTORY
});

@Component({
    selector: 'ap-autocomplete-panel',
    templateUrl: './autocomplete-panel.component.html',
    styleUrls: ['./autocomplete-panel.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    exportAs: 'apAutocomplete',
    host: {
        class: 'ap-autocomplete'
    },
    providers: [{ provide: AP_OPTION_PARENT_COMPONENT, useExisting: ApAutocompleteComponent }],
    imports: [NgClass]
})
export class ApAutocompleteComponent implements AfterContentInit {
    private readonly changeDetectorRef = inject(ChangeDetectorRef);

    // The @ViewChild query for TemplateRef here needs to be static because some code paths
    // lead to the overlay being created before change detection has finished for this component.
    // Notably, another component may trigger `focus` on the autocomplete-trigger.

    /** @docs-private */
    @ViewChild(TemplateRef, { static: true }) template: TemplateRef<any>;

    /** Element for the panel containing the autocomplete options. */
    @ViewChild('panel') panel: ElementRef;

    /** @docs-private */
    @ContentChildren(ApAutocompleteOptionComponent, { descendants: true })
    options: QueryList<ApAutocompleteOptionComponent>;

    /**
     * Specify the width of the autocomplete panel.  Can be any CSS sizing value, otherwise it will
     * match the width of its host.
     */
    readonly panelWidth = input<string | number>(undefined);

    /**
     * Autocomplete options. List of strings
     */
    public readonly autocompleteOptions = input<string[] | Record<string, any>>(undefined);

    /** Event that is emitted whenever an option from the list is selected. */
    @Output() readonly optionSelected: EventEmitter<ApOption> = new EventEmitter<ApOption>();

    /** Whether the autocomplete panel is open. */
    get isOpen(): boolean {
        return this._isOpen && this.showPanel;
    }

    /**
     * Whether the first option should be highlighted when the autocomplete panel is opened.
     * Can be configured globally through the `AP_AUTOCOMPLETE_DEFAULT_OPTIONS` token.
     */
    @Input()
    get autoActiveFirstOption(): boolean {
        return this._autoActiveFirstOption;
    }

    set autoActiveFirstOption(value: boolean) {
        this._autoActiveFirstOption = coerceBooleanProperty(value);
    }

    /** Manages active item in option list based on key events. */
    _keyManager: ActiveDescendantKeyManager<ApAutocompleteOptionComponent>;

    /** Whether the autocomplete panel should be visible, depending on option length. */
    showPanel = false;

    _isOpen = false;

    _classList: { [key: string]: boolean } = {};

    /** Unique ID to be used by autocomplete trigger's "aria-owns" property. */
    id: string;

    private _autoActiveFirstOption: boolean;

    constructor() {
        const defaults = inject<ApAutocompleteDefaultOptions>(AP_AUTOCOMPLETE_DEFAULT_OPTIONS);

        this._autoActiveFirstOption = !!defaults.autoActiveFirstOption;
        _uniqueAutocompleteIdCounter++;
        this.id = `ap-autocomplete-${_uniqueAutocompleteIdCounter}`;
    }

    ngAfterContentInit(): void {
        this._keyManager = new ActiveDescendantKeyManager<ApAutocompleteOptionComponent>(this.options).withWrap();
        // Set the initial visibility state.
        this._setVisibility();
    }

    /**
     * Sets the panel scrollTop. This allows us to manually scroll to display options
     * above or below the fold, as they are not actually being focused when active.
     */
    _setScrollTop(scrollTop: number): void {
        if (this.panel) {
            this.panel.nativeElement.scrollTop = scrollTop;
        }
    }

    /**
     * Sets the panel scrollTop. This allows us to manually scroll to display options
     * above or below the fold, as they are not actually being focused when active.
     */
    _determineScrollTop(optionIndex: number): void {
        const option = this.options.get(optionIndex);

        if (!option) {
            return;
        }
        const elementHeight = option.getHostElement().offsetHeight;
        const panelHeight = this.panel.nativeElement.offsetHeight;
        const newScrollPosition = _getOptionScrollPosition(optionIndex, elementHeight, this._getScrollTop(), panelHeight);

        this._setScrollTop(newScrollPosition);
    }

    /** Returns the panel's scrollTop. */
    _getScrollTop(): number {
        return this.panel ? this.panel.nativeElement.scrollTop : 0;
    }

    /** Panel should hide itself when the option list is empty. */
    _setVisibility(): void {
        this.showPanel = !!this.options.length;
        this._setVisibilityClasses(this._classList);
        this.changeDetectorRef.markForCheck();
    }

    /** Emits the `select` event. */
    _emitSelectEvent(option: ApAutocompleteOptionComponent): void {
        this.optionSelected.emit(option.value());
    }

    /** Sets the autocomplete visibility classes on a classlist based on the panel is visible. */
    private _setVisibilityClasses(classList: { [key: string]: boolean }): void {
        classList['ap-autocomplete-visible'] = this.showPanel;
        classList['ap-autocomplete-hidden'] = !this.showPanel;
    }
}
